import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';
import {
  Answer,
  Question,
  QuestionAddressValidation,
  QuestionInPage,
  Questionnaire,
  QuestionnaireAnswer,
  QuestionnairePage,
  QuestionnaireWithQuestions,
  QuestionType,
} from 'src/app/core/models/questionnaire.model';
import { QuestionnaireService } from 'src/app/core/services/survey.service';
import { ClaimsService } from 'src/app/core/services/claims.service';

import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { TranslateService } from '@ngx-translate/core';
import { EventTypes } from 'src/app/core/models/events.model';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { EventService } from 'src/app/core/services/event.service';
import { Survey_answersService } from 'src/app/core/services/survey_answers.service';
import { UserService } from 'src/app/core/services/user.service';
import { v4 as uuidv4 } from 'uuid';
import { runConditions } from '../../../utils/hideif';
import {
  caseAllowsSimpleQuestionnaire,
  isEffectivelyElective,
  isQuestionSkippableInSimpleQuestionnaire,
  isSimpleQuestionnaireSessionActive,
  markQuestionnaireSkippedInSession,
} from '../../../utils/elective';
import { pageHasBlankQuestion, pageHasPendingRequiredQuestion } from '../../../utils/questionnaire-required';
import { ActionsModalUiComponent } from '../../simple/actions-modal-ui/actions-modal.ui.component';
import { FillInfoService } from 'src/app/core/services/fill-info';
import { EligibilityService } from './core/eligibility.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { v4 as uuidv4_import } from 'uuid';
import { HasUnsavedChanges } from 'src/app/core/guards/unsaved-changes.guard';
import { QuestionnaireAnswersStateService } from 'src/app/core/services/questionnaire-answers-state.service';
import { QuestionnaireAnswerService } from 'src/app/core/services/questionnaire-answer.service';
import { AddressValidationPromptComponent } from '../address-validation-prompt/address-validation-prompt.component';
import {
  AddressValidationPromptAddress,
  AddressValidationPromptData,
  AddressValidationPromptResult,
} from '../address-validation-prompt/address-validation-prompt.types';

interface QuestionnaireErrors {
  page: Record<string, string>;
  global: Record<string, string>;
}

interface Errors {
  errors: Record<string, string>;
}
export interface PageErrors extends Errors {
  page: string;
  isValid: boolean;
}

@Component({
  selector: 'app-questionnaire-answer',
  templateUrl: './questionnaire-answer.component.html',
  styleUrls: ['./questionnaire-answer.component.scss'],
  providers: [QuestionnaireAnswersStateService],
})
export class QuestionnaireAnswerComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  // Inputs
  @Input('claimId') claimId!: string; //= 'b016dd10-ebbd-4ea7-aed3-b2a0fd14fad8';
  @Input('questionnaireId') questionnaireId!: string; // = 'a4aad8d5-6074-4b88-b3f7-042c6e5a754a';
  @Input('goToProgressSaved') askForUseSavedAnswers: boolean = true;
  @Input('fixedFooter') fixedFooter: boolean = true;

  @Output('completed') complete: EventEmitter<QuestionnaireAnswer> =
    new EventEmitter();

  addressValidation: QuestionAddressValidation[] = [];

  questionnaire!: Questionnaire;
  /** CPK-4846: cp_case.configuration.simple_questionnaire of this claim's case — fail-safe false. */
  caseAllowsSimpleQuestionnaire = false;
  questionsMap: { [id: string]: QuestionInPage } = {};
  navstack: {
    page: string;
    order: number;
    isActive: boolean;
  }[] = [];
  pages: QuestionnairePage[] = [];

  user!: any;

  questionnaireAnswer$ = new BehaviorSubject<QuestionnaireAnswer | null>(null);
  questionnaireAnswer: QuestionnaireAnswer | null = null;
  pageValid$: BehaviorSubject<PageErrors | null> =
    new BehaviorSubject<PageErrors | null>(null);

  skipMap: Record<string, boolean> = {};
  errors: QuestionnaireErrors = {
    page: {},
    global: {},
  };

  isPageValid = true;
  /**
   * CPK-4802: page id of the last pageValid$ evaluation. isPageValid alone is
   * stale until the active page emits — true by default on load and carried over
   * from the previous page on navigation — so canGoNext() only trusts it when it
   * belongs to the page on screen.
   */
  private validatedPageId: string | null = null;
  isQuestionnaireValid = true;
  showPrevButton: boolean = false;
  isPreviousAllowed: boolean = false;
  isNextAllowed: boolean = false;

  loading$ = new BehaviorSubject<{
    loading: boolean;
    message?: string;
  }>({
    loading: false,
  });
  loadingPage = false;
  footerHeight = 0;

  subscriptions: Subscription[] = [];

  private _savedAnswersSnapshot: string = '';
  private _prefetchPromise: Promise<void> | null = null;
  private _prefetchResolved = false;
  private _prefetchAnswersSaved: string = '';
  private _footerResizeObserver?: ResizeObserver;
  private _touchStartY: number = 0;
  private _pullToRefreshDialogOpen: boolean = false;
  private readonly _onTouchStart = (e: TouchEvent): void => {
    this._touchStartY = e.touches[0].clientY;
  };
  private readonly _onTouchMove = (e: TouchEvent): void => {
    if (this.matDialog.openDialogs.length > 0) return;
    if (!this.hasUnsavedChanges()) return;
    const deltaY = e.touches[0].clientY - this._touchStartY;
    if (window.scrollY === 0 && deltaY > 80) {
      e.preventDefault();
      if (!this._pullToRefreshDialogOpen) {
        this._pullToRefreshDialogOpen = true;
        const dialogRef = this.matDialog.open(ActionsModalUiComponent, {
          data: {
            title: this.translate.instant('UnsavedChangesTitle'),
            description: this.translate.instant('UnsavedChangesDescription'),
            cancelText: this.translate.instant('UnsavedChangesStay'),
            confirmText: this.translate.instant('UnsavedChangesReload'),
          },
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            window.location.reload();
          } else {
            this._pullToRefreshDialogOpen = false;
          }
        });
      }
    }
  };

  constructor(
    private ss: QuestionnaireService,
    private ds: DynamicService<any>,
    private sas: Survey_answersService,
    private us: UserService,
    private cs: ClaimsService,
    private fillInfoService: FillInfoService,
    private eligibilityService: EligibilityService,
    private matDialog: MatDialog,
    private toastService: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private ee: EventService,
    private router: Router,
    private eventService: EventService,
    private claimsService: ClaimsService,
    private storageService: StorageService,
    private answersState: QuestionnaireAnswersStateService,
    private qas: QuestionnaireAnswerService,
  ) { }

  ngOnInit() {
    this._initObservables();
    this._init();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this._footerResizeObserver?.disconnect();
  }

  @ViewChild('footerBar')
  set footerBar(ref: ElementRef<HTMLElement> | undefined) {
    this._footerResizeObserver?.disconnect();
    this._footerResizeObserver = undefined;

    if (!this.fixedFooter || !ref) return;

    const element = ref.nativeElement;
    this._footerResizeObserver = new ResizeObserver(() => {
      const isAnchored = getComputedStyle(element).position === 'fixed';
      const height = isAnchored
        ? Math.ceil(element.getBoundingClientRect().height)
        : 0;
      if (height === this.footerHeight) return;
      this.footerHeight = height;
      this.cdr.detectChanges();
    });
    this._footerResizeObserver.observe(element);
  }

  get footerSpacerPx(): number | null {
    if (!this.fixedFooter || !this.footerHeight) return null;
    return this.footerHeight + 16;
  }

  public hasVisibleErrors(): boolean {
    const errors = this.getErrorsList();
    return errors.page.length > 0 || errors.global.length > 0;
  }

  public hasUnsavedChanges(): boolean {
    return this._countUnsavedAnswers() > 0;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- required for Safari cross-browser support
      event.returnValue = this.translate.instant('UnsavedChangesDescription');
    }
  }

  private _countUnsavedAnswers(): number {
    if (!this.questionnaireAnswer) return 0;
    const current = this.questionnaireAnswer.answers ?? {};
    let saved: Record<string, any> = {};
    try {
      saved = JSON.parse(this._savedAnswersSnapshot) as Record<string, any>;
    } catch {
      saved = {};
    }

    // Build set of slugs that belong to visible (non-skipped) pages only
    const visibleSlugs = new Set<string>();
    Object.values(this.questionsMap ?? {}).forEach((q: any) => {
      if (q.cp_question?.slug && !this.skipMap[q.page_id]) {
        visibleSlugs.add(q.cp_question.slug);
      }
    });

    // Normalize { value: null } as absent — avoids counting auto-cleared hidden questions
    const normalize = (val: any): string => {
      if (val === undefined || val === null) return 'null';
      if (typeof val === 'object' && 'value' in val && val.value === null) return 'null';
      return JSON.stringify(val);
    };

    const allSlugs = new Set([...Object.keys(current), ...Object.keys(saved)]);
    let count = 0;
    for (const slug of allSlugs) {
      if (!visibleSlugs.has(slug)) continue; // skip hidden-page questions
      if (normalize(current[slug]) !== normalize(saved[slug])) {
        count++;
      }
    }
    return count;
  }



  /**
   * Initialize Context functions
   */
  private _initObservables() {
    const laodingSub$ = this.loading$.subscribe({
      next: ({ loading, message }) => {
        if (loading) {
          // this.toastService.info(message || 'Loading...');
        }
      },
    });
    const answer$ = this.questionnaireAnswer$
      .pipe(
        filter((e) => !!e),
        debounceTime(50)
      )
      .subscribe({
        next: (answer) => {
          this.evalSkipIf();
          this.eligibilityService.evaluate(answer);
          this.evalQuestionnaireErrors();
          this.isPreviousAllowed = this.canGoPrev();
          this.isNextAllowed = this.canGoNext();
          this.questionnaireAnswer = answer;
        },
      });
    const pageValid$ = this.pageValid$
      .pipe(
        filter((e) => !!e),
        debounceTime(50)
      )
      .subscribe({
        next: (page) => {
          // console.log('pageValid on sub => ', page);
          this.evalSkipIf();
          this.evalPageErrors(page!);
          this.evalQuestionnaireErrors();
          this.isPreviousAllowed = this.canGoPrev();
          this.isNextAllowed = this.canGoNext();
          if (this.isNextAllowed) {
            this._triggerPrefetch();
          } else {
            this._prefetchPromise = null;
            this._prefetchResolved = false;
          }
        },
      });

    this.subscriptions.push(...[laodingSub$, answer$, pageValid$]);
  }

  private async _init() {
    const user = await this.us.getCurrentUser();

    if (user) {
      if (user.primary_role === 'CLIENT') {
        const { users } = await this.cs.getUserForClaimId(this.claimId);
        const isAssociated = users.some((u: any) => u.id === user.id);
        if (isAssociated) {
          this.user = await this.us.getWithPII(user.id!);
        }
      } else {
        const { claims } = await this.cs.getUserForClaimId(this.claimId);
        const ownerPermission = claims?.[0]?.cp_claim_permission?.find(
          (p: any) => p.permission_type === 'OWNER'
        );
        this.user = await this.us.getWithPII(ownerPermission?.principal_id);
      }
    }

    if(!this.user) {
      //TODO: IR PARA TELA DE ERRO AO GERAR QUESTIONARIO
    }

    // Check claimId and questionnaireId
    if (!this.claimId || !this.questionnaireId) {
      // console.error('ClaimId or questionnaireId not set');
      this.router.navigate(['client']);
    }

    const questionnaire = await this.getQuestionnaire();
    this.questionnaire = questionnaire.questionnaire;
    this.questionsMap = questionnaire.questions_map;
    this.caseAllowsSimpleQuestionnaire = await this.loadCaseAllowsSimpleQuestionnaire();

    // Inicializar eligibility service com as regras do case
    await this.eligibilityService.initialize(
      this.claimId,
      this.questionnaireId,
      this.questionsMap
    );
    // Mount Navstack
    this.mountNavstack(questionnaire);

    // Get questionnaire answers if exists
    const answers = await this.getQuestionnaireAnswer(this.user);

    this.questionnaireAnswer$.next(answers);
    this.answersState.setQuestionnaireId(this.questionnaireId);
    this.answersState.setAll(answers?.answers ?? {});
    this._savedAnswersSnapshot = JSON.stringify(answers.answers ?? {});

    await this.sendEvent()

    try {
    } catch (error) {
    } finally {
      this.loading$.next({
        loading: false,
      });
    }
  }

  private async sendEvent(): Promise<any> {

    const caseId = await this.getCaseIdByClaimId();

    // Determinar o tipo de evento baseado no nome do questionário
    const questionnaireName = this.questionnaire?.name?.toLowerCase() || '';
    
    let eventTypeValue: EventTypes | null = null;
    
    if (questionnaireName.includes('elegib')) {
      eventTypeValue = EventTypes.ELEGIBILITY_QUEST_LOAD_PAGE;
    } else if (questionnaireName.includes('identif')) {
      eventTypeValue = EventTypes.IDENTIFICATION_QUEST_LOAD_PAGE;
    } else {
      // Default: não dispara evento se não identificar o tipo
      return;
    }

    const contextData: Object = {
      caseId: caseId,
    };

    try{
      await this.eventService.pushNew(eventTypeValue, contextData);
    }catch(error) {
      console.error('Event not sended, ', error);
    }
  }

  /**
   * CPK-4846: only looked up inside a Simple Questionnaire session — outside it the
   * flag can't change anything, so a regular client pays no extra query.
   */
  private async loadCaseAllowsSimpleQuestionnaire(): Promise<boolean> {
    if (!isSimpleQuestionnaireSessionActive()) return false;

    try {
      const claimWithCase = await this.claimsService.getClaimWithCase(this.claimId);
      return caseAllowsSimpleQuestionnaire(claimWithCase?.cp_case?.configuration);
    } catch (error) {
      console.error('[QUESTIONNAIRE_ANSWER] Error loading case simple_questionnaire flag', error);
      return false;
    }
  }

  private async getCaseIdByClaimId(): Promise<string> {

    let caseId: string = '';

    const claimId = this.claimId;

    if (!claimId) {
      console.warn('claimId not founded on URL');
      return caseId;
    }

    try{
       const getClaimsWithCase: any = await this.claimsService.getClaimWithCase(claimId);

      if(!getClaimsWithCase?.case_id){
        console.warn('CaseID not founded.')
        return caseId;
      }

      caseId = getClaimsWithCase.case_id;
      return caseId;

    }catch(error){
      console.error('Error consulting caseID, ', error);
      return caseId;
    }

  }

  private async getQuestionnaire() {
    this.loading$.next({
      loading: true,
      message: 'Loading questionnaire...',
    });
    // Get the questionnaire
    const questionnaire = await this.ss.getQuestionnaireWithQuestionsById(
      this.questionnaireId
    );

    if (!questionnaire) {
      // console.error('Questionnaire not found');
      this.router.navigate(['client']);
    }

    return questionnaire;
  }

  private mountNavstack({ questionnaire }: QuestionnaireWithQuestions) {
    const firstPage = questionnaire.cp_questionnaire_page.find(
      (cpqp: any) => cpqp.id === questionnaire.first_page
    );

    if (!firstPage) {
      // console.error('First page not found');
      return [];
    }

    this.navstack.push({
      page: firstPage.id,
      order: 0,
      isActive: true,
    });
    this.pages.push(firstPage);

    this.pushNextPageOnNavstack(questionnaire, firstPage, 1);

    return this.navstack;
  }

  private pushNextPageOnNavstack(
    questionnaire: Questionnaire,
    currentPage: QuestionnairePage,
    order: number
  ) {
    if (!currentPage.next_page_id) {
      return;
    }

    const nextPage = questionnaire.cp_questionnaire_page.find(
      (cpqp) => cpqp.id === currentPage.next_page_id
    );

    if (!nextPage) {
      // console.error('Next page not found');
      return;
    }

    this.navstack.push({
      page: nextPage.id,
      order: order,
      isActive: false,
    });
    this.pages.push(nextPage);

    this.pushNextPageOnNavstack(questionnaire, nextPage, order + 1);
  }

  private async getQuestionnaireAnswer(currentUser: any): Promise<QuestionnaireAnswer> {
    this.loading$.next({
      loading: true,
      message: 'Loading possible answers...',
    });

    if (!currentUser) {
      // console.error('Current User error!');
      this.router.navigate(['client']);
    }
    const newAnswer: QuestionnaireAnswer = {
      claim_id: this.claimId,
      created_at: new Date(),
      id: uuidv4(),
      answers: {},
      questionnaire_id: this.questionnaire!.id,
      completed_at: null,
      responsible_user_id: currentUser.id!,
      campaign_data: null,
      history: [],
      navstack: [],
      started_at: new Date(),
      status: 'OPEN',
      user_data: this.us.getFingerprint(),
    };

    try {
      const questionnaireWithAnswersList =
        await this.ss.getSurveyWithAnswersByClaimAndQuestionnaire(
          this.claimId,
          this.questionnaireId
        );

      if (!questionnaireWithAnswersList) {
        // console.error('Questionnaire Not Found not found');
        this.router.navigate(['client']);
      }
      const questionnaireWithAnswers = questionnaireWithAnswersList[0];
      if (!questionnaireWithAnswers) {
        // console.error('Questionnaire Not Found not found');
        return newAnswer;
      }

      const answers = questionnaireWithAnswers.answers;
      if (!answers) {
        // console.error('Answers not found');
        return newAnswer;
      }

      if (answers.length === 0) {
        return newAnswer;
      }

      // Find the most recent answer to check its status
      const latestAnswer = answers.reduce((prev, current) => {
        return new Date(prev.started_at).getTime() >
          new Date(current.started_at).getTime()
          ? prev
          : current;
      });

      // If the questionnaire is already submitted, don't ask to recover progress
      if (latestAnswer.status === 'SUBMITTED') {
        const populated = this.populateAnswers(answers);
        return populated;
      }

      // Check Progress?
      if (!this.askForUseSavedAnswers) {
        const populated = this.populateAnswers(answers);
        this.forceFirstBlankPage(populated);
        return populated;
      } else {
        // Check if user wants to go to the last answres saved or init a new one
        const dialogRef = this.matDialog.open(ActionsModalUiComponent, {
          data: {
            title: 'Progress found',
            description: 'Do you want to recover your questionnaire progress?',
            cancelText: 'No, reset progress.',
            confirmText: 'Yes, continue.',
          },
        });
        return new Promise((resolve, reject) => {
          dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
              // Reset progress: reuse existing answer ID to overwrite on save
              const latestAnswerId = answers.reduce((prev, current) => {
                return new Date(prev.started_at).getTime() >
                  new Date(current.started_at).getTime()
                  ? prev
                  : current;
              }).id;
              
              this.clearQuestionnaire().finally(() => {
                // Reuse existing ID to overwrite the previous answer
                newAnswer.id = latestAnswerId;
                newAnswer.started_at = new Date();
                newAnswer.answers = {};
                newAnswer.navstack = [];
                newAnswer.status = 'OPEN';
                newAnswer.history = [];
                newAnswer.completed_at = null;
                this.resetToFirstPage(newAnswer);
                return resolve(newAnswer);
              });
            } else {
              // Continue
              const populated = this.populateAnswers(answers);
              this.forceFirstBlankPage(populated);
              resolve(populated);
            }
          });
        });
      }
    } catch (error) {
      console.error('Error on get questionnaire answer');
      return newAnswer;
    }
  }

  private populateAnswers(answers: QuestionnaireAnswer[]): QuestionnaireAnswer {
    // Get most recent answer from the list (started_at: Date ISO string)
    const answerList: QuestionnaireAnswer = answers.reduce((prev, current) => {
      return new Date(prev.started_at).getTime() >
        new Date(current.started_at).getTime()
        ? prev
        : current;
    });

    const newNavstack: any[] = Object.assign([], this.navstack);

    if (!answerList.navstack) {
      answerList.navstack = this.navstack;
    }

    if (answerList.navstack.length !== newNavstack.length) {
      answerList.navstack = newNavstack;
    }

    if (this.isOldNavstackFormat(answerList.navstack)) {
      const uniquePagesList = [...new Set(answerList.navstack)];
      const newNavstack = uniquePagesList
        .map((pageId: string, index: number) => {
          return {
            page: this.questionnaire.cp_questionnaire_page.find(
              (cpqp: any) => cpqp.id === pageId
            ),
            order: index,
            isActive: false,
          };
        })
        .filter((n) => n.page)
        .map((n, i) => ({ ...n, order: i }));
      newNavstack[0].isActive = true;
      answerList.navstack = newNavstack;
    }

    answerList.navstack = answerList.navstack.map((ns, i) => ({
      ...ns,
      page: newNavstack[i].page,
    }));

    (this.navstack as any) = answerList.navstack;
    return answerList;
  }

  private isOldNavstackFormat(navstack: any[]): boolean {
    return typeof navstack[0] === 'string';
  }

  private isQuestionSkippableInSimpleQuestionnaire(question: Question): boolean {
    return isQuestionSkippableInSimpleQuestionnaire(question, this.caseAllowsSimpleQuestionnaire);
  }

  private isEffectivelyElective(question: Question): boolean {
    return isEffectivelyElective(question, this.caseAllowsSimpleQuestionnaire);
  }

  private findFirstPendingRequiredPageIndex(
    answer: QuestionnaireAnswer,
    caseAllowsSkip: boolean = this.caseAllowsSimpleQuestionnaire
  ): number | null {
    for (let i = 0; i < this.navstack.length; i++) {
      const page = this.getQuestionnairePage(this.navstack[i].page);
      if (!page) continue;

      if (pageHasPendingRequiredQuestion(page, caseAllowsSkip, answer)) return i;
    }

    return null;
  }

  /**
   * CPK-4878: in a Simple Questionnaire session a can_skip_question question left empty
   * passes blockSubmitWithPendingRequired (it is effectively elective there), but it is
   * still required for the client. Same navstack walk, with the skip turned off.
   */
  private hasSkippedRequiredPending(): boolean {
    if (!this.questionnaireAnswer || !this.caseAllowsSimpleQuestionnaire) return false;
    return this.findFirstPendingRequiredPageIndex(this.questionnaireAnswer, false) !== null;
  }

  /**
   * CPK-4899: resuming an OPEN answer ("Yes, continue." or no dialog) lands on the
   * first blank question — elective and skippable ones included — for client and
   * agent alike, so whoever resumes sees what is still missing.
   */
  private findFirstBlankPageIndex(answer: QuestionnaireAnswer): number | null {
    for (let i = 0; i < this.navstack.length; i++) {
      const page = this.getQuestionnairePage(this.navstack[i].page);
      if (!page) continue;

      if (pageHasBlankQuestion(page, answer)) return i;
    }

    return null;
  }

  private forceFirstBlankPage(answer: QuestionnaireAnswer): void {
    const targetIndex = this.findFirstBlankPageIndex(answer);
    if (targetIndex === null) return;

    this.activatePage(answer, targetIndex);
  }

  /** CPK-4899: "No, reset progress." starts over from the very first page. */
  private resetToFirstPage(answer: QuestionnaireAnswer): void {
    this.activatePage(answer, 0);
  }

  private activatePage(answer: QuestionnaireAnswer, targetIndex: number): void {
    this.navstack.forEach((ns, i) => (ns.isActive = i === targetIndex));
    answer.navstack = this.navstack;
  }

  /**
   * CPK-4855: isPageValid starts true and only turns false once the page emits
   * pageValid$, so a click right after the last page renders could save SUBMITTED
   * with empty answers — which the client can never resend (CPK-4246). Checks the
   * exact answers about to be saved with the same required rule the guide uses and,
   * on a gap, keeps the answer OPEN and sends the user to the first pending page.
   */
  private blockSubmitWithPendingRequired(): boolean {
    if (!this.questionnaireAnswer) return false;

    const targetIndex = this.findFirstPendingRequiredPageIndex(this.questionnaireAnswer);
    if (targetIndex === null) return false;

    console.warn('[QUESTIONNAIRE_ANSWER] Final submit blocked: required question pending', {
      questionnaireId: this.questionnaire?.id,
      claimId: this.claimId,
      pageIndex: targetIndex,
    });

    this.navstack.forEach((ns, i) => (ns.isActive = i === targetIndex));
    this.isPreviousAllowed = this.canGoPrev();
    this.isNextAllowed = this.canGoNext();
    this.toastService.error(this.translate.instant('RequiredQuestionsPending'));
    return true;
  }

  /**
   * Obtém informações de inelegibilidade do serviço
   */
  getIneligibilityInfo() {
    return this.eligibilityService.getIneligibilityInfo();
  }

  // View functions

  public getQuestionnairePage(pageId: string) {
    return this.pages.find((p) => p.id === pageId);
  }

  public atualPage() {
    return this.navstack.find((ns) => ns.isActive);
  }
  public getProgressBarValue() {
    // Get state on x/100% value on progress bar
    // Get current page index on navstack and check the referal page on navstack position, then calculate the percentage
    const currentPageIndex = this.navstack.findIndex((ns) => ns.isActive);
    const totalPages = this.navstack.length;

    // % = (currentPageIndex + 1) / totalPages * 100
    return ((currentPageIndex + 1) / totalPages) * 100;
  }
  public getAnswersObservable(): Observable<QuestionnaireAnswer | null> {
    return this.questionnaireAnswer$.asObservable();
  }
  public onChangeQuestionnaireAnswer(event: QuestionnaireAnswer) {
    this.questionnaireAnswer$.next(event);
    this.answersState.setAll(event?.answers ?? {});
  }
  public onChangePageValid(event: PageErrors) {
    this.pageValid$.next(event);
  }
  public onPageLoading(event: boolean) {
    this.loadingPage = event;
  }
  public getErrorsList() {
    const errors: {
      page: string[];
      global: string[];
    } = {
      page: [],
      global: [],
    };
    if (!this.isPageValid) errors.page.push(...Object.values(this.errors.page));
    if (!this.isQuestionnaireValid)
      errors.page.push(...Object.values(this.errors.global));

    return errors;
  }

  public async goNext() {
    const canGoNext = this.canGoNext();
    if (canGoNext) {
      const atualPageIndex = this.navstack.findIndex((ns) => ns.isActive);

      if (atualPageIndex === this.navstack.length - 1) {
        // console.log('Finishing survey...');
        if (this.blockSubmitWithPendingRequired()) return;
        try {
          this.loading$.next({
            loading: true,
            message: 'Saving survey...',
          });
          // CPK-4799: a hover on this same button may have already fired a
          // background prefetch save (see onNextButtonHover/_triggerPrefetch).
          // Awaiting it here serializes the two requests instead of letting
          // them race — the backend's create-vs-update check isn't atomic,
          // so two concurrent first-time saves for the same questionnaire
          // can produce a spurious "already exists" error. A failed prefetch
          // is swallowed: it must never block the real submit below, which
          // is independent and always runs.
          if (this._prefetchPromise) {
            const pendingPrefetch = this._prefetchPromise;
            this._prefetchPromise = null;
            this._prefetchResolved = false;
            this._prefetchAnswersSaved = '';
            await pendingPrefetch.catch(() => {});
          }
          if (this.hasSkippedRequiredPending()) {
            await this.saveQuestionnaireWithSkippedPending();
          } else {
            await this.saveFullQuestionnaire();
          }
          await this.updateAllQuestionWithFillDBConfiguration(atualPageIndex);
          await this.updateAllQuestionWithSplitDbConfiguration(atualPageIndex);
        } catch (error) {
          console.error('Error on save full questionnaire', error);
          this.toastService.error(this.getQuestionnaireSaveErrorMessage(error));
          // Don't proceed if save failed
        } finally {
          this.loading$.next({
            loading: false,
          });
        }
      } else {
        // console.log('Go next page...');
        try {
          this.loading$.next({
            loading: true,
          });
          this.navstack[atualPageIndex].isActive = false;

          // Find on navstack the closest next page that is not in skipIf
          const closestPageIndex = this.navstack
            .slice(atualPageIndex + 1)
            .findIndex((ns) => !this.skipMap[ns.page]);

          if (closestPageIndex === -1) {
            console.error('No next page found');
            return;
          } else {
            const nextPageIndex = atualPageIndex + 1 + closestPageIndex;
            this.navstack[nextPageIndex].isActive = true;
          }
          this.isPreviousAllowed = this.canGoPrev();
          this.isNextAllowed = this.canGoNext();

          const prefetch = this._prefetchPromise;
          const prefetchResolved = this._prefetchResolved;
          const prefetchAnswers = this._prefetchAnswersSaved;
          this._prefetchPromise = null;
          this._prefetchResolved = false;
          this._prefetchAnswersSaved = '';

          const currentAnswers = prefetchResolved
            ? JSON.stringify(this.questionnaireAnswer!.answers ?? {})
            : null;
          const answersMatch = prefetchResolved && prefetchAnswers === currentAnswers;

          if (answersMatch) {
            // Fast path: answers already persisted — save navstack update in background
            this.questionnaireAnswer!.status = 'OPEN';
            this.questionnaireAnswer!.navstack = this.navstack;
            this.ee.pushNew(EventTypes.QUESTIONNAIRE_NEXT_PAGE, {
              questionnaire_id: this.questionnaire?.id,
              claim_id: this.claimId,
            }).then().catch();
            this.saveQuestionnaire().catch(() => {});
          } else if (prefetch) {
            // In-flight: reuse the existing request rather than firing a duplicate
            try {
              await prefetch;
              this.questionnaireAnswer!.status = 'OPEN';
              this.questionnaireAnswer!.navstack = this.navstack;
              this.ee.pushNew(EventTypes.QUESTIONNAIRE_NEXT_PAGE, {
                questionnaire_id: this.questionnaire?.id,
                claim_id: this.claimId,
              }).then().catch();
              this.saveQuestionnaire().catch(() => {});
            } catch {
              await this.savePartialQuestionnaire();
            }
          } else {
            await this.savePartialQuestionnaire();
          }

          await this.updateAllQuestionWithFillDBConfiguration(atualPageIndex);
          await this.updateAllQuestionWithSplitDbConfiguration(atualPageIndex);
        } catch (error) {
          console.error('Error on save partial questionnaire', error);
          this.toastService.error(this.getQuestionnaireSaveErrorMessage(error));
          this.goPrev();
        } finally {
          this.loading$.next({
            loading: false,
          });
        }
      }
    }
  }

  public onNextButtonHover(): void {
    this._triggerPrefetch();
  }

  private _triggerPrefetch(): void {
    if (this._prefetchPromise || !this.questionnaireAnswer || !this.isNextAllowed) return;
    // The prefetch always saves status OPEN. Once the final submit went through,
    // the loading$ toggle destroys and recreates the last page, which re-emits
    // pageValid$ and lands here again — an OPEN save at that point silently
    // reverts the SUBMITTED answer before the guide re-reads it (seen in stg
    // after CPK-4799 cleared _prefetchPromise ahead of the final submit).
    if (this.questionnaireAnswer.status === 'SUBMITTED') return;
    this._prefetchResolved = false;
    const answersSnapshot = JSON.stringify(this.questionnaireAnswer.answers ?? {});
    this._prefetchAnswersSaved = '';
    this._prefetchPromise = (this.sas.submit({
      ...this.questionnaireAnswer,
      status: 'OPEN',
      navstack: [...this.navstack],
    }) as Promise<any>)
      .then(() => {
        this._prefetchResolved = true;
        this._prefetchAnswersSaved = answersSnapshot;
        this._savedAnswersSnapshot = answersSnapshot;
      })
      .catch(() => {
        this._prefetchPromise = null;
        this._prefetchResolved = false;
      });
  }

  public goPrev() {
    const canGoPrev = this.canGoPrev();
    if (canGoPrev) {
      const atualPageIndex = this.navstack.findIndex((ns) => ns.isActive);
      console.log('Can go prev', this.navstack[atualPageIndex]);

      if (atualPageIndex === 0) {
        // console.log('First page...');
      } else {
        // console.log('Go prev page...');
        this.navstack[atualPageIndex].isActive = false;

        // Find on navstack a closest page that is not in skipIf
        const closestPageIndexReversed = this.navstack
          .slice(0, atualPageIndex)
          .reverse()
          .findIndex((ns) => !this.skipMap[ns.page]);

        if (closestPageIndexReversed === -1) {
          console.error('No page found');
          return;
        } else {
          const closestPageIndex =
            atualPageIndex - 1 - closestPageIndexReversed;
          this.navstack[closestPageIndex].isActive = true;
        }
        this.isPreviousAllowed = this.canGoPrev();
        this.isNextAllowed = this.canGoNext();
      }
    }
  }

  private canGoPrev(): any {
    const atualPageIndex = this.navstack.findIndex((ns) => ns.isActive);
    if (atualPageIndex === 0) {
      this.isPreviousAllowed = false;
      return false;
    }
    const pagesBefore = this.navstack
      .slice(0, atualPageIndex)
      .filter((ns) => !this.skipMap[ns.page]);
    if (pagesBefore.length === 0) {
      this.isPreviousAllowed = false;
      return false;
    }
    this.isPreviousAllowed = true;
    return true;
  }
  private canGoNext(): boolean {
    if (!this.isActivePageValidated()) return false;
    const canProceed = this.isPageValid && this.isQuestionnaireValid;
    if (!canProceed) return false;
    if (this.isFileUploadRequiredAndMissing()) return false;
    return true;
  }

  private evalSkipIf(): void {
    this.questionnaire.cp_questionnaire_page.forEach((page) => {
      const pageHideIfCondition = page.page_config?.skip_if;
      // console.log('Page Skip If => ', pageHideIfCondition);

      if (!pageHideIfCondition) {
        this.skipMap[page.id] = false;
        return;
      } else {
        const a = this.questionnaireAnswer$.value as any;
        // console.log('skip => data to be checked', a);
        this.skipMap[page.id] = runConditions(pageHideIfCondition, a);
      }

      // console.log('New Skip Map => ', this.skipMap);
    });

    // console.log('Skip Map => ', this.skipMap);
  }
  private isActivePageValidated(): boolean {
    const activePage = this.atualPage();
    return !!activePage && this.validatedPageId === activePage.page;
  }

  private evalPageErrors(pageErrors: PageErrors): void {
    this.validatedPageId = pageErrors.page;
    if (pageErrors.isValid) {
      this.isPageValid = true;

      this.errors.page = {};
    } else {
      this.isPageValid = false;
      this.errors.page = pageErrors.errors;
    }
  }
  private evalQuestionnaireErrors(): void {
    // TODO: Evaluate questionnaire errors
    this.isQuestionnaireValid = true;
  }

  private async savePartialQuestionnaire() {
    this.loading$.next({
      loading: true,
      message: 'Saving partial survey...',
    });

    this.questionnaireAnswer!.status = 'OPEN';
    this.questionnaireAnswer!.navstack = this.navstack;

    this.ee
      .pushNew(EventTypes.QUESTIONNAIRE_NEXT_PAGE, {
        questionnaire_id: this.questionnaire?.id,
        claim_id: this.claimId,
      })
      .then()
      .catch();

    await this.saveQuestionnaire();
  }
  /**
   * CPK-4878: the agent skipped can_skip_question questions — save OPEN (no
   * completed_at, no QUESTIONNAIRE_COMPLETED) so the client can still answer them,
   * since a SUBMITTED answer can never be resent (CPK-4246). The session mark lets
   * the guide move on for the rest of this phone session only.
   */
  private async saveQuestionnaireWithSkippedPending() {
    this.loading$.next({
      loading: true,
      message: 'Saving survey...',
    });

    this.questionnaireAnswer!.status = 'OPEN';
    this.questionnaireAnswer!.navstack = this.navstack;
    this.questionnaireAnswer!.completed_at = null;

    await this.saveQuestionnaire();

    markQuestionnaireSkippedInSession(this.claimId, this.questionnaire?.id ?? this.questionnaireId);
    this.loading$.next({ loading: false });
    this.toastService.info(this.translate.instant('QuestionnaireSavedWithPendingQuestions'));
    this.complete.next(this.questionnaireAnswer!);
  }
  private async saveFullQuestionnaire() {
    this.loading$.next({
      loading: true,
      message: 'Saving survey...',
    });

    this.questionnaireAnswer!.status = 'SUBMITTED';
    this.questionnaireAnswer!.navstack = this.navstack;
    this.questionnaireAnswer!.completed_at = new Date();

    try {
      const saved = await this.saveQuestionnaire();
      if (!saved) {
        throw new Error('Failed to save questionnaire');
      }

      // Only emit events and complete if save was successful
      this.ee
        .pushNew(EventTypes.QUESTIONNAIRE_COMPLETED, {
          questionnaire_id: this.questionnaire?.id,
          claim_id: this.claimId,
        })
        .then()
        .catch();

      // CPK-4239: hide the saving spinner before the confirmation dialog —
      // resolveAddressValidationPrompt is a no-op if nothing failed/was flagged.
      this.loading$.next({ loading: false });
      await this.resolveAddressValidationPrompt();

      this.complete.next(this.questionnaireAnswer!);
    } catch (error) {
      console.error('Error on save full questionnaire', error);
      // Reset status if save failed
      this.questionnaireAnswer!.status = 'OPEN';
      this.questionnaireAnswer!.completed_at = null;
      throw error; // Re-throw to be handled by caller
    }
  }
  private async updateAllQuestionWithFillDBConfiguration(pageIndex: number) {
    try {
      console.log('[FILL_INFO] Iniciando processamento de fillInfo para pageIndex:', pageIndex);

      // Get atual page
      const atualPage = this.getQuestionnairePage(
        this.navstack[pageIndex].page
      );
      if (!atualPage) {
        console.log('[FILL_INFO] Página não encontrada');
        return;
      }

      // Get all questions on page
      const questions = atualPage.cp_question_in_page.map(
        (cqip) => cqip.cp_question
      );
      if (questions.length === 0) {
        console.log('[FILL_INFO] Nenhuma questão encontrada na página');
        return;
      }

      // Get all questions with fill configuration
      const questionWithFillConfig = questions.filter(
        (q) => !!q.configuration?.fillInfo
      );

      if (questionWithFillConfig.length === 0) {
        console.log('[FILL_INFO] Nenhuma questão com fillInfo configurado');
        return;
      }

      console.log('[FILL_INFO] Encontradas', questionWithFillConfig.length, 'questões com fillInfo');

      // Process each question with FillInfoService
      for (const q of questionWithFillConfig) {
        const fillInfo = q.configuration?.fillInfo;
        if (!fillInfo) continue;

        // 🔧 FIX: Buscar resposta do questionnaireAnswer ao invés de q.answer
        // Isso garante que funciona tanto para novas respostas quanto para progresso recuperado
        const answer = this.questionnaireAnswer?.answers?.[q.slug];
        
        console.log('[FILL_INFO] Processando questão:', {
          slug: q.slug,
          hasAnswer: !!answer,
          answerValue: answer?.value,
          hasAdditionalInfo: !!answer?.addtional_information,
        });

        // Skip if no answer value
        if (!answer || ((answer.value === null || answer.value === undefined) && !answer.addtional_information)) {
          console.log('[FILL_INFO] Pulando questão sem resposta:', q.slug);
          continue;
        }

        try {
          console.log('[FILL_INFO] Chamando fillInfoService.process para:', q.slug);
          // Use FillInfoService - suporta V1 e V2
          await this.fillInfoService.process({
            config: fillInfo,
            answer: answer,
            user: this.user!,
          });
          console.log('[FILL_INFO] FillInfo processado com sucesso para:', q.slug);
        } catch (error) {
          console.error('[FILL_INFO] Error processing fillInfo for question', q.id, error);
        }
      }

      console.log('[FILL_INFO] Processamento concluído');
      return;
    } catch (error) {
      console.error(
        '[FILL_INFO] Error in updateAllQuestionWithFillDBConfiguration',
        error
      );
      return;
    }
  }

  private async updateAllQuestionWithSplitDbConfiguration(pageIndex: number) {
    try {
      console.log('[SPLIT_INFO] Iniciando processamento de splitInfo para pageIndex:', pageIndex);

      // Get atual page
      const atualPage = this.getQuestionnairePage(
        this.navstack[pageIndex].page
      );
      if (!atualPage) {
        console.log('[SPLIT_INFO] Página não encontrada');
        return;
      }

      // Get all questions on page
      const questions = atualPage.cp_question_in_page.map(
        (cqip) => cqip.cp_question
      );
      if (questions.length === 0) {
        console.log('[SPLIT_INFO] Nenhuma questão encontrada na página');
        return;
      }

      // Get all questions with fill configuration
      const questionWithFillConfig = questions.filter(
        (q) => !!q.configuration?.splitInfo
      );

      if (questionWithFillConfig.length === 0) {
        console.log('[SPLIT_INFO] Nenhuma questão com splitInfo configurado');
        return;
      }

      console.log('[SPLIT_INFO] Encontradas', questionWithFillConfig.length, 'questões com splitInfo');

      for (const q of questionWithFillConfig) {
        const splitInfo = q.configuration?.splitInfo;
        if (!splitInfo) continue;

        const from = splitInfo.from;
        const update = splitInfo.source;
        // const updateType = fillInfo.updateType || 'string';
        const valueSource = splitInfo.valueSource || 'value';

        const filters = splitInfo.filters.map(
          (f: { op: string; col: string; val: string }) => ({
            ...f,
            val: f.val === '$userId' ? this.user!.id : f.val,
          })
        );
        if (!from || !update || filters.length === 0) continue;

        await this.ds.load(from);

        // 🔧 FIX: Buscar resposta do questionnaireAnswer ao invés de q.answer
        const answer = this.questionnaireAnswer?.answers?.[q.slug];

        let valueToUpdate: any;

        if (valueSource === 'addtional_information') {
          valueToUpdate = answer?.addtional_information;
        } else {
          valueToUpdate = answer?.value;
        }

        console.log('[SPLIT_INFO] Processando questão:', {
          slug: q.slug,
          valueSource,
          valueToUpdate,
        });

        if (!valueToUpdate) {
          console.log('[SPLIT_INFO] Pulando questão sem valor:', q.slug);
          continue;
        }

        const valReformed = this.createResultForSplitConfig(
          valueToUpdate,
          splitInfo
        );



        try {
          const r = await this.ds.updatefillQuery(
            from,
            update,
            filters,
            valReformed,
            'string'
          );
        } catch (error) {
          console.error('Error on update question with split configuration');
        }
      }
    } catch (error) { }
  }

  createResultForSplitConfig(data: string, config: any) {
    // Dividimos a string com base no separador configurado
    const values = data.split(config.separator);

    const result: any = {};

    // Processamos cada campo definido no array de fields
    config.fields.forEach((field: any) => {
      let value;

      if (field.position === 'first') {
        // Pega a primeira parte
        value = values[0];
      } else if (field.position === 'last') {
        // Pega a última parte
        value = values[values.length - 1];
      } else if (field.position === 'middle') {
        // Pega as partes intermediárias
        value = values.slice(1, values.length - 1).join(' ');
      } else if (field.position === 'index' && field.index >= 0) {
        // Caso o campo tenha um index específico
        value = values[field.index] || ''; // Garante que o valor exista
      }

      // Atribuímos o valor para o campo no resultado final
      result[field.db_column] = value;
    });

    // Agora persistimos o resultado no banco de dados
    return result;
  }

  private async clearQuestionnaire() {
    // No need to delete - we reuse the existing answer ID and overwrite it
    // This maintains audit history and is more efficient (update vs delete+insert)
    return;
  }

  private async saveQuestionnaire() {
    try {
      const ret = await this.sas.submit(this.questionnaireAnswer!);
      this.addressValidation = ret?.address_validation ?? [];
      this.applyValidatedCoordinates();
      this._savedAnswersSnapshot = JSON.stringify(this.questionnaireAnswer?.answers ?? {});
      return true;
    } catch (error) {
      console.error('ERROR during questionnaire submit', error);
      throw error; // Re-throw to be handled by caller
    }
  }

  private applyValidatedCoordinates(): void {
    const answers = this.questionnaireAnswer?.answers;
    if (!answers) return;

    for (const item of this.addressValidation) {
      const validated = item.address;
      const answer = answers[item.questionSlug];
      if (!validated || !answer?.addtional_information) continue;
      if (!('latitude' in validated) && !('longitude' in validated)) continue;

      answer.addtional_information = {
        ...answer.addtional_information,
        latitude: validated['latitude'] ?? null,
        longitude: validated['longitude'] ?? null,
        ...(validated['coordinates_source'] && { coordinates_source: validated['coordinates_source'] }),
      };
    }
  }

  // CPK-4239: submit already ran address validation server-side (see
  // runAddressValidationOnSubmit); this opens the same confirm/edit dialog used
  // by Profile whenever the result came back failed or flagged, looping on edits
  // until it passes, gets a human override, or the user cancels.
  private async resolveAddressValidationPrompt(): Promise<void> {
    const pending = this.addressValidation.find(
      (v) => v.status === 'failed' || v.flagged === true || v.llm_suspicion?.suspicious === true
    );
    if (!pending || !this.questionnaireAnswer?.id) return;

    let current = pending;

    while (true) {
      // CPK-4239: once the retry limit is hit, the Questionnaire flow accepts the
      // address as-is instead of showing the "maximum attempts reached" modal —
      // unlike Profile, which still requires an explicit human confirmation click.
      // CPK-5157: no confirmed_override here any more. Submit already validated the
      // address and stored that verdict; an automatic override replaced it with a
      // "user_confirmed" the client never gave.
      if (current.retry_limit_reached) {
        return;
      }

      const promptResult = await this.openAddressValidationPrompt(current);
      if (!promptResult || promptResult.action === 'cancelled') {
        return;
      }

      if (promptResult.action === 'confirmed_override') {
        try {
          await this.qas.confirmAddressValidation(
            this.questionnaireAnswer.id,
            current.questionSlug,
            'confirmed_override'
          );
        } catch (error) {
          console.error('ERROR confirming address validation override', error);
        }
        return;
      }

      if (promptResult.action === 'modified_address' && promptResult.address) {
        const address = this.toStructuredAddress(promptResult.address);

        try {
          const { details } = await this.qas.confirmAddressValidation(
            this.questionnaireAnswer.id,
            current.questionSlug,
            'modified_address',
            address
          );

          // CPK-4456: confirm-questionnaire-address just persisted this correction straight to
          // cp_pii, but updateAllQuestionWithFillDBConfiguration() (called right after this prompt
          // resolves, from goNext()) re-sends whatever is still in `answers[slug]` via fillInfo —
          // without updating it here too, that replay used the pre-correction address and clobbered
          // the fix a moment later. Mirrors what confirm-questionnaire-address writes server-side.
          this.questionnaireAnswer.answers = this.questionnaireAnswer.answers ?? {};
          this.questionnaireAnswer.answers[current.questionSlug] = {
            ...this.questionnaireAnswer.answers[current.questionSlug],
            value: this.formatAddressLine(address),
            addtional_information: details.address ?? address,
            address_validation: details.payload,
          } as Answer;

          // CPK-4293: once retries are exhausted, the backend persists whatever the user
          // just typed (see confirm-questionnaire-address) and, since CPK-5157, stores the
          // verdict for it too — so treat retry_limit_reached as resolved here and stop
          // looping, instead of reopening the prompt's single-button "limit reached" state.
          // The user gets exactly one real modal (2 choices); after that, they move on.
          const isResolved =
            details.payload.retry_limit_reached === true ||
            (details.payload.status === 'passed' &&
              !details.payload.flagged &&
              details.payload.llm_suspicion?.suspicious !== true);

          if (isResolved) {
            return;
          }

          current = {
            ...current,
            status: 'failed',
            flagged: details.payload.flagged,
            possible_next_action: details.payload.possible_next_action as any,
            validation_granularity: details.payload.validation_granularity ?? undefined,
            formatted_address: details.payload.formatted_address ?? undefined,
            response_id: details.payload.response_id ?? undefined,
            retry_limit_reached: details.payload.retry_limit_reached,
            caller_unlimited_retries: details.payload.caller_unlimited_retries,
            llm_suspicion: details.payload.llm_suspicion,
            address,
          };
        } catch (error) {
          console.error('ERROR revalidating modified address', error);
          return;
        }
      }
    }
  }

  private openAddressValidationPrompt(
    item: QuestionAddressValidation
  ): Promise<AddressValidationPromptResult | undefined> {
    const data: AddressValidationPromptData = {
      enteredAddress: this.toPromptAddress(item.address),
      validationPayload: {
        status: item.status,
        possible_next_action: item.possible_next_action,
        validation_granularity: item.validation_granularity,
        formatted_address: item.formatted_address,
        response_id: item.response_id,
        retry_limit_reached: item.retry_limit_reached,
        caller_unlimited_retries: item.caller_unlimited_retries,
        llm_suspicion: item.llm_suspicion,
      },
      previousResponseId: item.response_id ?? null,
    };

    const ref = this.matDialog.open<
      AddressValidationPromptComponent,
      AddressValidationPromptData,
      AddressValidationPromptResult
    >(AddressValidationPromptComponent, {
      data,
      autoFocus: false,
      disableClose: true,
      panelClass: 'address-validation-prompt-dialog',
    });

    return firstValueFrom(ref.afterClosed());
  }

  private toPromptAddress(address?: Record<string, any>): AddressValidationPromptAddress {
    return {
      streetNumber: address?.['streetNumber'] ?? null,
      streetName: address?.['streetName'] ?? null,
      city: address?.['city'] ?? null,
      neighborhood: address?.['neighborhood'] ?? null,
      state: address?.['state'] ?? null,
      county: address?.['county'] ?? null,
      country: address?.['country'] ?? null,
      postcode: address?.['postcode'] ?? null,
      postalCode: address?.['postalCode'] ?? null,
      buildingName: address?.['buildingName'] ?? null,
      unitNumber: address?.['unitNumber'] ?? null,
    };
  }

  private toStructuredAddress(promptAddress: AddressValidationPromptAddress): Record<string, any> {
    return {
      streetNumber: promptAddress.streetNumber ?? '',
      streetName: promptAddress.streetName ?? '',
      unitNumber: promptAddress.unitNumber ?? '',
      buildingName: promptAddress.buildingName ?? '',
      neighborhood: promptAddress.neighborhood ?? '',
      city: promptAddress.city ?? '',
      state: promptAddress.state ?? '',
      country: promptAddress.country ?? '',
      postalCode: promptAddress.postalCode ?? '',
      // CPK-4239 fix: this explicit-field rebuild was dropping resolvedVia (the modal
      // sets it in getEditedAddress(), but it never survived this conversion), so a
      // correction made here always persisted with no resolvedVia at all.
      resolvedVia: promptAddress.resolvedVia ?? 'manual',
    };
  }

  // Mirrors confirm-questionnaire-address/index.ts's formatAddressLine(), which in turn mirrors
  // question-world-address.component.ts's onFieldChange() composite — kept in sync so the
  // in-memory answer's `value` matches what the backend just persisted.
  private formatAddressLine(address: Record<string, any>): string {
    const { streetNumber, streetName, city, state, postalCode, country } = address;
    return [streetNumber, streetName, city, state, postalCode, country]
      .filter(Boolean)
      .join(', ');
  }

  private getQuestionnaireSaveErrorMessage(error: any): string {
    if (error?.status >= 500) {
      return this.translate.instant('ErrorSavingQuestionnaire');
    }
    return (
      error?.error?.message ||
      error?.message ||
      this.translate.instant('ErrorSavingQuestionnaire')
    );
  }

  /**
   * Verifica se a página atual exige upload de arquivo obrigatório e se o(s) arquivo(s) foi(ram) enviado(s)
   */
  private isFileUploadRequiredAndMissing(): boolean {
    const atualPage = this.atualPage();
    if (!atualPage) return false;
    const page = this.getQuestionnairePage(atualPage.page);
    if (!page) return false;
    const fileQuestions = page.cp_question_in_page.filter(
      (q) =>
        q.cp_question.response_type === 'MULTI_FILE' &&
        !this.isEffectivelyElective(q.cp_question)
    );
    if (fileQuestions.length === 0) return false;
    return fileQuestions.some((q) => {
      const answer = this.questionnaireAnswer?.answers[q.cp_question.slug];
      if (q.cp_question.response_type === 'MULTI_FILE') {
        return (
          !answer ||
          !answer.value ||
          (Array.isArray(answer.value) && answer.value.length === 0) ||
          (typeof answer.value === 'string' && answer.value === '')
        );
      }
      return false;
    });
  }
}
